/* -------------------------------------------------------------------------- */
/*  src/components/table/Catalog.tsx — full-featured UI + **new SQLite schema**     */
/* -------------------------------------------------------------------------- */

import React, { useState, useEffect, useCallback } from "react";
import { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";
import { registerTableFromUrl, arrowTableToObjects, useToast } from "@/lib";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Database, Cloud, Plus, FileX } from "lucide-react";
import { useSQLiteDatabase } from "@/core/sqlite/useSQLiteDatabase";



/* --- helper now imported from lib/duckdb.ts --- */

/* ---------------------------- Type definitions --------------------------- */

export interface Dataset {
  id: string;
  name: string;
  title?: string; // NEW
  description?: string;
  partitioned?: boolean;
  io_manager_key?: string; // NEW
  r2_url?: string; // legacy
  data_url?: string; // NEW
  start_year?: number;
  start_month?: number;
  end_year?: number;
  end_month?: number;
  tags: string[];
  custom_commands?: string;
  created_at?: string;
  updated_at?: string;
}

export interface DictionaryColumn {
  id: string;
  column_name: string;
  data_type: string;
  description: string;
  ordinal_position: number;
}

interface CatalogProps {
  conn: any; // DuckDB WASM connection
  onTableRegistered: () => Promise<void>;
}

/* --------------------------- sql.js helpers ------------------------------ */

function parseRows(stmt: any) {
  const out: any[] = [];
  while (stmt.step()) out.push(stmt.getAsObject());
  stmt.free();
  return out;
}

/* ======================================================================== */
/*                               Component                                   */
/* ======================================================================== */

export default function Catalog({ conn, onTableRegistered }: CatalogProps) {
  const sqliteUrl = "https://fastopendata.org/mytest.sqlite";
  const { db, loading, error } = useSQLiteDatabase(sqliteUrl);
  const { toast } = useToast();

  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(
    null
  );
  const [detail, setDetail] = useState<{
    dataset: Dataset;
    columns: DictionaryColumn[];
  } | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  /* ------------------------------- load list ----------------------------- */
  useEffect(() => {
    if (!db) return;
    try {
      const stmt = db.prepare(
        `SELECT *, COALESCE(title,name) AS sort_key
         FROM   datasets
         ORDER  BY sort_key`
      );
      const rows = parseRows(stmt).map((r) => {
        try {
          return {
            ...r,
            tags: r.tags ? JSON.parse(r.tags) : [],
          };
        } catch (e) {
          console.warn(`Failed to parse tags for dataset ${r.name || r.id}:`, e);
          return { ...r, tags: [] };
        }
      });
      setDatasets(rows as Dataset[]);
    } catch (e: any) {
      toast({
        title: "Database error",
        description: e.message,
        variant: "destructive",
      });
    }
  }, [db, toast]);

  /* ------------------------------ load detail ---------------------------- */
  useEffect(() => {
    if (!db || !selectedDatasetId) return;
    try {
      const dsStmt = db.prepare(
        "SELECT * FROM datasets WHERE id = ? LIMIT 1"
      );
      dsStmt.bind([selectedDatasetId]);
      const dsRow = dsStmt.step() ? dsStmt.getAsObject() : null;
      dsStmt.free();

      if (!dsRow) return;

      const colStmt = db.prepare(
        "SELECT * FROM dictionary_columns WHERE dataset_id = ? ORDER BY ordinal_position"
      );
      colStmt.bind([selectedDatasetId]);
      const cols = parseRows(colStmt) as DictionaryColumn[];

      let tags = [];
      try {
        tags = dsRow.tags ? JSON.parse(dsRow.tags) : [];
        if (!Array.isArray(tags)) tags = [];
        tags = tags.filter((tag) => typeof tag === "string");
      } catch (e) {
        console.warn(
          `Failed to parse tags for dataset detail ${dsRow.name || dsRow.id}:`,
          e
        );
      }

      const ds: Dataset = {
        ...dsRow,
        tags,
      };
      setDetail({ dataset: ds, columns: cols });
    } catch (e) {
      console.error("detail load error", e);
    }
  }, [db, selectedDatasetId]);

  /* ------------------------------ helpers -------------------------------- */

  const computeUrl = (ds: Dataset) =>
    ds.data_url?.trim()
      ? ds.data_url
      : ds.r2_url?.trim()
      ? ds.r2_url
      : `https://fastopendata.org/${encodeURIComponent(
          ds.name
        )}/${encodeURIComponent(ds.name)}.parquet`;

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const filteredDatasets = useCallback(() => {
    if (!debouncedSearchTerm.trim()) return datasets;
    const q = debouncedSearchTerm.toLowerCase();
    return datasets.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (d.title || "").toLowerCase().includes(q) ||
        (d.description || "").toLowerCase().includes(q) ||
        (Array.isArray(d.tags) &&
          d.tags.some((t) => typeof t === "string" && t.toLowerCase().includes(q)))
    );
  }, [datasets, debouncedSearchTerm]);

  /* -------------------------- bulk registration ------------------------- */

  const toggle = (id: string) =>
    setSelectedIds((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
    );

  const bulkRegister = async () => {
    if (!conn) return;
    setBulkLoading(true);
    const sel = datasets.filter((d) => selectedIds.includes(d.id));

    const results = await Promise.allSettled(
      sel.map((d) => registerTableFromUrl(conn, d.name, computeUrl(d)))
    );
    const ok = results
      .map((r, i) => [r.status, sel[i].name] as const)
      .filter(([s]) => s === "fulfilled")
      .map(([, n]) => n);

    if (ok.length) {
      toast({ title: "Success", description: `Registered: ${ok.join(", ")}` });
    }
    if (results.some((r) => r.status === "rejected")) {
      toast({
        title: "Some tables failed",
        description: "See console for details",
        variant: "destructive",
      });
    }

    setSelectedIds([]);
    setBulkLoading(false);
    await onTableRegistered();
  };

  /* -------------------------------- render ------------------------------ */

  if (loading)
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin h-8 w-8 border-4 rounded-full border-t-brand" />
      </div>
    );

  if (error)
    return (
      <DataSourceError
        title="Catalog load error"
        message={error.message}
        source="network"
        onRetry={() => window.location.reload()}
      />
    );

  return (
    <div className="flex flex-col h-full">
      {/* ---------------- header ---------------- */}
      <div className="bg-white rounded-lg shadow-sm p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-brand to-indigo-600 bg-clip-text text-transparent">
            Data Catalog
          </h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const filtered = filteredDatasets();
                setSelectedIds(
                  selectedIds.length === filtered.length
                    ? []
                    : filtered.map((d) => d.id)
                );
              }}
            >
              {(() => {
                const filtered = filteredDatasets();
                return selectedIds.length === filtered.length
                  ? "Deselect All"
                  : "Select All";
              })()}
            </Button>
            {selectedIds.length > 0 && (
              <Button
                variant="brand"
                size="sm"
                disabled={bulkLoading}
                onClick={bulkRegister}
              >
                {bulkLoading
                  ? "Registering…"
                  : `Register ${selectedIds.length} Tables`}
              </Button>
            )}
          </div>
        </div>

        <div className="relative">
          <input
            type="text"
            className="pl-10 h-10 bg-muted/50 w-full rounded-md text-sm border-0 focus:ring-2 focus:ring-brand"
            placeholder="Search datasets by name, title, description, or tags…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <svg
            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>

      {/* ---------------- grid ---------------- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* ---------- list ---------- */}
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-3 border-b bg-muted/30 text-sm font-medium">
              Available Datasets ({filteredDatasets().length})
            </div>
            <div className="h-[500px] overflow-y-auto custom-scrollbar">
              {(() => {
                const filtered = filteredDatasets();
                return filtered.length ? (
                  filtered.map((ds) => (
                    <div
                      key={ds.id}
                      className={`p-4 cursor-pointer hover:bg-muted/20 flex gap-3 ${
                        selectedDatasetId === ds.id ? "bg-muted/40" : ""
                      }`}
                      onClick={() => {
                        setSelectedDatasetId(ds.id);
                        setDetail(null);
                      }}
                      onDoubleClick={async () => {
                        try {
                          await registerTableFromUrl(
                            conn,
                            ds.name,
                            computeUrl(ds)
                          );
                          toast({ title: "Registered", description: ds.name });
                          await onTableRegistered();
                        } catch (e: any) {
                          toast({
                            title: "Registration failed",
                            description: e.message,
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(ds.id)}
                        onChange={() => toggle(ds.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {ds.title || ds.name}
                        </div>
                        <div className="text-xs text-muted-foreground line-clamp-2">
                          {ds.description}
                        </div>
                        {Array.isArray(ds.tags) && ds.tags.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {ds.tags.slice(0, 3).map((t, idx) => (
                              <span
                                key={`${t}-${idx}`}
                                className="text-xs bg-blue-50 text-blue-700 rounded-full px-2"
                              >
                                {typeof t === "string" ? t : ""}
                              </span>
                            ))}
                            {ds.tags.length > 3 && (
                              <span className="text-xs bg-gray-50 text-gray-500 rounded-full px-2">
                                +{ds.tags.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <FileX className="h-12 w-12 mb-3" />
                    No matching datasets
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* ---------- detail ---------- */}
        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow-sm">
            {detail ? (
              <>
                <div className="border-b p-5 bg-white flex justify-between">
                  <div>
                    <h2 className="text-xl font-bold">
                      {detail.dataset.title || detail.dataset.name}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {detail.dataset.description}
                    </p>
                  </div>
                  <Button
                    variant="brand"
                    onClick={async () => {
                      try {
                        await registerTableFromUrl(
                          conn,
                          detail.dataset.name,
                          computeUrl(detail.dataset)
                        );
                        toast({
                          title: "Registered",
                          description: detail.dataset.name,
                        });
                        await onTableRegistered();
                      } catch (e: any) {
                        toast({
                          title: "Registration failed",
                          description: e.message,
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    Register Table
                  </Button>
                </div>

                {/* source */}
                <div className="px-5 py-3 bg-muted/20 border-b text-xs">
                  <a
                    href={computeUrl(detail.dataset)}
                    className="text-blue-600 break-all"
                    target="_blank"
                  >
                    {computeUrl(detail.dataset)}
                  </a>
                </div>

                {/* columns */}
                <div className="p-5">
                  <h3 className="text-sm font-medium mb-2">
                    Columns ({detail.columns.length})
                  </h3>
                  <div className="h-[400px] overflow-y-auto overflow-x-auto border rounded custom-scrollbar">
                    <table className="min-w-full text-xs">
                      <thead className="bg-muted/40 sticky top-0">
                        <tr>
                          <th className="p-2 text-left">#</th>
                          <th className="p-2 text-left">Name</th>
                          <th className="p-2 text-left">Type</th>
                          <th className="p-2 text-left">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.columns.map((c) => (
                          <tr key={c.id} className="border-t">
                            <td className="p-2">{c.ordinal_position}</td>
                            <td className="p-2 font-mono">{c.column_name}</td>
                            <td className="p-2">{c.data_type}</td>
                            <td className="p-2">{c.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-[500px] text-muted-foreground">
                Select a dataset to view details
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
